import React from 'react';
import { connect } from 'react-redux';
import { withCookies } from 'react-cookie';
import _ from 'lodash';
import {Input, ListScreen} from '../components';
import {
    fetchAiChatbotQuestions,
    cleanAiChatbotQuestions,
    setUser,
    changeUserSetting,
    deleteAiChatbotQuestion,
    deleteAiChatbotQuestions,
    editAiChatbotQuestion,
} from '../actions';
import {
    __,
    formatAmount,
    formatDate,
    getCurrency,
    isEmptyString,
    request, toNumber,
} from '../functions';
import '../assets/styles/ai-chatbot-questions.css';
import DeleteIcon from "@mui/icons-material/Delete";
import {Tooltip} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import {AI_FUNCTION_NAMES} from "../config";

class AiChatbotQuestionsScreen extends ListScreen {
    title = __('AI chatbot - otázky a odpovede');

    state = { ...ListScreen.state, ...{
        loadingRefresh: false,
        lightbox: {
            delete: false,
            edit: false,
            create: false,
            multidelete: false,
        },
    }};

    async fetchList(page = 1, per_page = 0, filtered = {}, callback = null) {
        this.page = page;

        const { fetch, params } = this.props;

        // Nacitame zoznam
        await fetch(this, _.has(params, 'message_id') ? params.message_id : '', { page, per_page, filtered, callback });

        const { items } = this.props;

        if (!_.isEmpty(items.filtered)) {
            // Nastavime filter
            this.setState({ filtered: items.filtered });
        }

        if (!_.isEmpty(items.message_text)) {
            this.showLightbox('create', {
                loading: false,
                input: items.message_text,
                output: '',
            });
        }

        this.afterFetchList();
    }

    isValidPermission() {
        return this.hasPermission('aiaddons');
    }

    getTableName() {
        return 'ai-chatbot-questions';
    }

    getMultiselect() {
        return [
            {
                confirm: (ids, callback, callbackLoading) => this.showLightbox('multidelete', {
                    ids,
                    callback,
                    callbackLoading,
                }),
                icon: <DeleteIcon />,
                text: __('Zmazať'),
            },
        ];
    }

    getColumns() {
        return [
            <div>{__('ID')}</div>,
            <div>{__('Otázka')}</div>,
            <div>{__('Odpoveď')}</div>,
            <div>{__('Cena')}</div>,
            <div>{__('Vytvorené')}</div>,
            <div></div>,
        ];
    }

    getCells(item) {
        return [
            <div>{item.id}</div>,
            <div><Tooltip title={item.input}><div>{_.truncate(item.input, { length: 32 })}</div></Tooltip></div>,
            <div><Tooltip title={item.output}><div>{_.truncate(item.output, { length: 32 })}</div></Tooltip></div>,
            <div>{formatAmount(item.price, 'EUR', 8)}</div>,
            <div>{formatDate(item.created, 'dd.mm.yyyy hh:ii')}</div>,
            <div>
                {this.renderTableButton(
                    item.id,
                    __('Upraviť'),
                    <EditIcon />,
                    (callbackLoading, callback) => this.confirmEdit(item.id, item.input, item.output, callbackLoading, callback),
                )}
                {this.renderTableButton(
                    item.id,
                    __('Zmazať'),
                    <DeleteIcon />,
                    (callbackLoading, callback) => this.confirmDelete(item.id, callbackLoading, callback),
                )}
            </div>,
        ];
    }

    getTags() {
        const { loadingRefresh } = this.state;

        return [
            {
                name: __('Nová otázka'),
                onClick: () => this.showLightbox('create', {
                    loading: false,
                    input: '',
                    output: '',
                }),
                disabled: this.isDemo(),
            },
            {
                name: !loadingRefresh ? __('Aktualizovať AI chatbota') : this.renderLoading(18, 'inherit'),
                onClick: () => this.refreshChatbot(),
                disabled: this.isDemo(),
                color: 'secondary',
                className: `refresh-chatbot ${loadingRefresh ? 'refresh-chatbot-loading' : ''}`,
            },
        ];
    }

    getEmptyText() {
        return __('Zatiaľ nemáte žiadne otázky a odpovede');
    }

    confirmDelete(id, callbackLoading, callback) {
        this.showLightbox('delete', { id, callbackLoading, callback });
    }

    confirmEdit(id, input, output, callbackLoading, callback) {
        this.showLightbox('edit', { id, input, output, callbackLoading, callback });
    }

    onChangeCreate(type, value) {
        const { lightbox } = this.state;

        this.setState({ lightbox: { ...lightbox, create: { ...lightbox.create, [type]: value } } });
    }

    onChangeEdit(type, value) {
        const { lightbox } = this.state;

        this.setState({ lightbox: { ...lightbox, edit: { ...lightbox.edit, [type]: value } } });
    }

    refreshChatbot() {
        this.setState({ loadingRefresh: true });

        request('/ai-addons/prepare/chat').then(response => {
            const { status } = response.data;

            this.setState({ loadingRefresh: false });

            if (status === 'error') {
                this.showSnackbar('error', __('Nepodarilo sa aktualizovať AI chatbota'));
                return;
            }

            this.showSnackbar('success', __('AI chatbot bol aktualizovaný'));
        });
    }

    async delete() {
        const { deleteAiChatbotQuestion, items } = this.props;
        const { lightbox } = this.state;

        if (this.isDemo()) {
            this.showSnackbar('error', __('Otázku nie je možné zmazať na DEMO účte'))
            this.closeLightbox('delete');
            return;
        }

        // Zavolame loading
        lightbox.delete.callbackLoading();

        // Zavrieme lightbox
        this.closeLightbox('delete');

        // Zmazeme
        await deleteAiChatbotQuestion(this, lightbox.delete.id);

        // Zavolame callback
        lightbox.delete.callback();

        // Znovu nacitame zoznam od prvej stranky
        this.fetchList(1, 0, items.filtered);
    }

    async edit() {
        const { editAiChatbotQuestion } = this.props;
        const { lightbox } = this.state;

        if (isEmptyString(lightbox.edit.input)) {
            this.showSnackbar('error', __('Nie je vyplnená otázka'));
            return;
        } else if (isEmptyString(lightbox.edit.output)) {
            this.showSnackbar('error', __('Nie je vyplnená odpoveď'));
            return;
        }

        if (this.isDemo()) {
            this.showSnackbar('error', __('Otázku nie je možné upraviť na DEMO účte'))
            this.closeLightbox('edit');
            return;
        }

        // Zavolame loading
        lightbox.edit.callbackLoading();

        // Zavrieme lightbox
        this.closeLightbox('edit');

        // Zmazeme
        await editAiChatbotQuestion(this, lightbox.edit.id, lightbox.edit.input, lightbox.edit.output);

        // Zavolame callback
        lightbox.edit.callback();
    }

    async create() {
        const { lightbox } = this.state;

        if (isEmptyString(lightbox.create.input)) {
            this.showSnackbar('error', __('Nie je vyplnená otázka'));
            return;
        } else if (isEmptyString(lightbox.create.output)) {
            this.showSnackbar('error', __('Nie je vyplnená odpoveď'));
            return;
        }

        this.onChangeCreate('loading', true);

        request('/ai-chatbot-questions/add', { input: lightbox.create.input, output: lightbox.create.output }, 'POST').then(response => {
            const { status } = response.data;

            if (status === 'error') {
                this.onChangeCreate('loading', false);
                this.showSnackbar('error', __('Otázku sa nepodarilo pridať'));
                return;
            }

            // Refreshneme
            window.location = '/ai-chatbot/questions';
        });
    }

    async multiDelete() {
        const { deleteAiChatbotQuestions, items, params } = this.props;
        const { lightbox } = this.state;

        if (this.isDemo()) {
            this.showSnackbar('error', __('Otázky nie je možné zmazať na DEMO účte'))
            this.closeLightbox('delete');
            return;
        }

        // Zavrieme lightbox
        this.closeLightbox('multidelete');

        // Zobrazime loading
        lightbox.multidelete.callbackLoading();

        await deleteAiChatbotQuestions(this, lightbox.multidelete.ids, items.filtered);

        // Zavolame callback
        lightbox.multidelete.callback();

        // Znovu nacitame zoznam od prvej stranky
        this.fetchList(1, 0, items.filtered);
    }

    renderScreen() {
        const { items } = this.props;
        const { lightbox } = this.state;

        return (
            <div>
                {this.renderLightbox(
                    'create',
                    __('Nová otázka'),
                    !_.isEmpty(lightbox.create) ? (lightbox.create.loading ? this.renderLoading(20) : <div className="create-ai-chatbot-question">
                        <Input
                            label={__('Otázka')}
                            value={lightbox.create.input}
                            onChange={value => this.onChangeCreate('input', value)}
                            multiline
                        />
                        <Input
                            label={__('Odpoveď')}
                            value={lightbox.create.output}
                            onChange={value => this.onChangeCreate('output', value)}
                            multiline
                        />
                    </div>) : null,
                    __('Uložiť'),
                    __('Zrušiť'),
                    () => this.create()
                )}
                {this.renderLightbox(
                    'edit',
                    __('Úprava otázky'),
                    !_.isEmpty(lightbox.edit) ? (lightbox.edit.loading ? this.renderLoading(20) : <div className="edit-ai-chatbot-question">
                        <Input
                            label={__('Otázka')}
                            value={lightbox.edit.input}
                            onChange={value => this.onChangeEdit('input', value)}
                            multiline
                        />
                        <Input
                            label={__('Odpoveď')}
                            value={lightbox.edit.output}
                            onChange={value => this.onChangeEdit('output', value)}
                            multiline
                        />
                    </div>) : null,
                    __('Uložiť'),
                    __('Zrušiť'),
                    () => this.edit()
                )}
                {this.renderLightbox(
                    'delete',
                    __('Naozaj chcete zmazať túto otázku?'),
                    null,
                    __('Áno'),
                    __('Nie'),
                    () => this.delete()
                )}
                {this.renderLightbox(
                    'multidelete',
                    __('Naozaj chcete zmazať označené otázky?'),
                    null,
                    __('Áno'),
                    __('Nie'),
                    () => this.multiDelete()
                )}
                {this.renderSnackbar()}
            </div>
        );
    }
}

const stateToProps = ({ ai_chatbot_questions, user }) => ({ items: ai_chatbot_questions, user });

export default withCookies(connect(stateToProps, {
    fetch: fetchAiChatbotQuestions,
    clean: cleanAiChatbotQuestions,
    setUser,
    changeUserSetting,
    deleteAiChatbotQuestion,
    deleteAiChatbotQuestions,
    editAiChatbotQuestion,
})(AiChatbotQuestionsScreen));
